import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowCounterClockwise, Bank, Buildings, Calendar, CalendarBlank, CalendarCheck, CalendarDots, Check, CheckCircle, Coins, DeviceMobile, EnvelopeSimple, Eye, EyeSlash, FilePdf, FolderSimple, Hash, HouseLine,
  IdentificationCard, Key, LinkSimple, LockSimple, LockSimpleOpen, MapPin, MapTrifold, PauseCircle, Phone, Receipt, SealCheck, Signpost, Sparkle,
  Trash, User, UsersThree, Wallet, Wrench, type Icon,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, PanelZeile, SchalterZeile, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { istRhythmus, ladeMandatPdf, mandatEmpfaenger, mandatFehlt, RHYTHMEN, type MandatDaten, type Rhythmus } from '../../../../../lib/eigentuemer/sepaMandat';
import { passwortFehler, passwortVorschlag } from '../../../../../lib/immobilien/portal';
import type { KontaktKanal } from '../../../../../lib/kontakt/kontaktKanaele';
import { PORTAL_ANZEIGE } from '../../../../../lib/eigentuemer/liste';
import {
  gebeEigPortalFrei, ladeEigKanaele, loescheEigentuemer, loeseEigPortal, pruefeLoeschen, setzeEigPortalStatus, setzeEigStatus, speichereBank, speichereStammdaten,
  verknuepfeEigPortal,
} from '../../../../../lib/eigentuemer/aktionen';
import { kandidatText, type ZugangKandidat } from '../../../../../lib/eigentuemer/portalVerbund';
import {
  istInaktiv, loeschenMoeglich, loeschText, stammAusEigentuemer, stammFehler, stammGeaendert, type EigAnredeWahl, type EigStammEntwurf, type LoeschPruefung,
} from '../../../../../lib/eigentuemer/stammdaten';
import { meldeEigentuemerGeaendert } from '../../../../../lib/eigentuemer/useEigentuemerDetail';
import { DatenZeile, ibanText } from '../../../objekte/einheit/bereiche/teile';
import { NeuesPasswortSenden } from '../../../../../components/portal/NeuesPasswortSenden';
import { TestphaseKarte } from '../../../../../components/portal/TestphaseKarte';
import { testphaseFuerDatensatz } from '../../../../../lib/portal/schalter';
import { sendeZugangsdaten, zugangsMeldung } from '../../../../../lib/portal/zugangsdaten';
import { useEigSeite } from '../EigentuemerSeitenKontext';
import { ZustellwegeAuswahl } from '../../../../../components/schreiben/ZustellwegeAuswahl';
import { WeitereKontaktwege } from '../../../../../components/kontakt/WeitereKontaktwege';
import { kanaeleGleich } from '../../../../../lib/kontakt/kontaktKanaele';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const t = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());
const BEREICH_STIL: React.CSSProperties = {
  width: '100%', borderRadius: 15, padding: 14, border: 'none', resize: 'vertical', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)',
  fontSize: 15, color: 'var(--ink)', outline: 'none', fontFamily: 'inherit',
};

/** Rückfrage bei ungespeicherten Änderungen (Escape, „Alle Widgets“). */
function useVerlassen(geaendert: boolean): [boolean, (v: boolean) => void] {
  const { registriereVerlassen } = useEigSeite();
  const [verwerfen, setVerwerfen] = useState(false);
  useEffect(() => {
    registriereVerlassen(geaendert ? () => setVerwerfen(true) : null);
    return () => registriereVerlassen(null);
  }, [geaendert, registriereVerlassen]);
  return [verwerfen, setVerwerfen];
}

function Verwerfen({ text, onAbbrechen }: { text: string; onAbbrechen: () => void }) {
  const { registriereVerlassen, schliessePanel } = useEigSeite();
  return (
    <BestaetigenDialog titel="Änderungen verwerfen?" text={text} bestaetigenLabel="Verwerfen" icon={ArrowCounterClockwise}
      onBestaetigen={() => { onAbbrechen(); registriereVerlassen(null); schliessePanel(); }} onAbbrechen={onAbbrechen} />
  );
}

const Aktionslink = ({ href, icon: IconKomp, children }: { href: string; icon: Icon; children: React.ReactNode }) => (
  <a href={href} className="immo-hover-flaeche inline-flex items-center"
    style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
    <IconKomp weight="bold" size={14} />{children}
  </a>
);

// ── Stammdaten & Kontakt (Screens 36, 37) ──────────────────────────────────────
export function StammdatenPanel() {
  const s = useEigSeite();
  const navigate = useNavigate();
  const eig = s.daten.eigentuemer;
  const [start, setStart] = useState<EigStammEntwurf>(() => stammAusEigentuemer(eig));
  const [e, setE] = useState<EigStammEntwurf>(start);
  const [kanaele, setKanaele] = useState<KontaktKanal[] | null>(null);
  const [kanalFehler, setKanalFehler] = useState<string | null>(null);
  const [kanaeleStart, setKanaeleStart] = useState<KontaktKanal[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [pruefung, setPruefung] = useState<LoeschPruefung | 'laedt' | null>(null);
  const [loescht, setLoescht] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof EigStammEntwurf>(k: K) => (v: EigStammEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const fehler = stammFehler(e);
  // #520: auch weitere E-Mails/Telefonnummern zählen als Änderung
  const geaendert = stammGeaendert(e, start) || !kanaeleGleich(kanaele, kanaeleStart);
  const [verwerfen, setVerwerfen] = useVerlassen(geaendert);
  const inaktiv = istInaktiv(eig.status);
  const firma = e.anrede === 'Firma';

  useEffect(() => {
    let aktiv = true;
    ladeEigKanaele(eig).then(k => { if (aktiv) { setKanaele(k); setKanaeleStart(k); } }).catch((err: unknown) => { if (aktiv) setKanalFehler(fehlerText(err)); });
    return () => { aktiv = false; };
  }, [eig]);

  const speichere = async () => {
    if (Object.keys(fehler).length) { zeige('Bitte die markierten Felder prüfen', true); return; }
    if (!kanaele) { zeige(`E-Mail/Telefon nicht geladen${kanalFehler ? `: ${kanalFehler}` : ''}`, true); return; }
    setLaeuft(true);
    try {
      const gespeichert = await speichereStammdaten(eig.id, e, start, kanaele);
      setKanaele(gespeichert);
      setKanaeleStart(gespeichert);
      setStart(e);
      zeige('Stammdaten gespeichert');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const setzeStatus = async (aktiv: boolean) => {
    setLaeuft(true);
    try {
      await setzeEigStatus(eig.id, aktiv, t(eig.vertragsende) || null, s.heute);
      setPruefung(null);
      zeige(aktiv ? `${s.name} wieder aktiv` : `${s.name} auf inaktiv gesetzt`);
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht geändert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const starteLoeschen = async () => {
    setPruefung('laedt');
    try { setPruefung(await pruefeLoeschen(eig.id)); }
    catch (err) { setPruefung(null); zeige(`Zuordnungen konnten nicht geprüft werden: ${fehlerText(err)}`, true); }
  };
  const loesche = async () => {
    setLoescht(true);
    try {
      await loescheEigentuemer(eig.id);
      s.registriereVerlassen(null);
      navigate('/admin/eigentuemer', { replace: true });
    } catch (err) {
      setLoescht(false);
      setPruefung(null);
      zeige(`Nicht gelöscht: ${fehlerText(err)}`, true);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Persönliche Daten</KartenTitel>
        <SegmentedControl<EigAnredeWahl> ariaLabel="Anrede" hoehe={34} className="self-start" wert={e.anrede} onChange={setze('anrede')}
          segmente={[{ key: 'Frau', label: 'Frau', icon: User }, { key: 'Herr', label: 'Herr', icon: User }, { key: 'Eheleute', label: 'Eheleute', icon: User }, { key: 'Firma', label: 'Firma', icon: Buildings }]} />
        {e.anrede === '' && t(eig.anrede) && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Anrede bisher: „{t(eig.anrede)}“</span>}
        <FeldRaster minBreite={180}>
          {(firma || e.firma) && <TextFeld label="Firmenname" icon={Buildings} wert={e.firma} onChange={setze('firma')} placeholder="z. B. Immobilien GmbH" fehler={fehler.firma} voll />}
          <TextFeld label={firma ? 'Ansprechpartner Vorname' : 'Vorname'} icon={User} wert={e.vorname} onChange={setze('vorname')} />
          <TextFeld label={firma ? 'Ansprechpartner Nachname' : 'Nachname'} icon={User} wert={e.nachname} onChange={setze('nachname')} fehler={fehler.name} />
          <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} fehler={fehler.email} autoComplete="off" />
          <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} autoComplete="off" />
          <TextFeld label="Steuer-ID" icon={Hash} inputMode="numeric" wert={e.steuerId} onChange={setze('steuerId')} placeholder="11-stellig" fehler={fehler.steuerId} />
          <TextFeld label="Steuer-Nr." icon={Hash} wert={e.steuerNr} onChange={setze('steuerNr')} placeholder="z. B. 031 245 12345" />
        </FeldRaster>
        {kanaele && <WeitereKontaktwege kanaele={kanaele} onChange={setKanaele} disabled={laeuft} />}
        <ZustellwegeAuswahl art="eigentuemer" id={eig.id} />
      </InnenKarte>

      <InnenKarte style={GLAS}>
        <KartenTitel>Anschrift</KartenTitel>
        <FeldRaster minBreite={180}>
          <TextFeld label="Straße" icon={Signpost} wert={e.strasse} onChange={setze('strasse')} />
          <TextFeld label="Nr." icon={Hash} wert={e.hausnummer} onChange={setze('hausnummer')} />
          <TextFeld label="PLZ" icon={MapPin} inputMode="numeric" wert={e.plz} onChange={setze('plz')} fehler={fehler.plz} />
          <TextFeld label="Stadt" icon={MapTrifold} wert={e.stadt} onChange={setze('stadt')} />
        </FeldRaster>
        <textarea value={e.notizen} onChange={ev => setze('notizen')(ev.target.value)} rows={3} aria-label="Zusatzinfos" placeholder="Zusätzliche Vereinbarungen oder Informationen …" style={BEREICH_STIL} />
        <SchalterZeile label={inaktiv ? 'Status: Inaktiv' : 'Status: Aktiv'} icon={inaktiv ? PauseCircle : CheckCircle} an={!inaktiv} disabled={laeuft}
          onChange={an => { void setzeStatus(an); }} zusatz={inaktiv ? 'Wird in der Liste unter „Ehemalig“ geführt' : undefined} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !geaendert} onClick={() => { void speichere(); }}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </ImmoButton>
        </div>
      </InnenKarte>

      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {e.telefon.trim() ? <Aktionslink href={`tel:${e.telefon.replace(/[^\d+]/g, '')}`} icon={Phone}>Anrufen</Aktionslink>
          : <ImmoButton groesse={44} icon={Phone} disabled title="Keine Telefonnummer hinterlegt">Anrufen</ImmoButton>}
        {e.email.trim() ? <Aktionslink href={`mailto:${e.email.trim()}`} icon={EnvelopeSimple}>E-Mail</Aktionslink>
          : <ImmoButton groesse={44} icon={EnvelopeSimple} disabled title="Keine E-Mail hinterlegt">E-Mail</ImmoButton>}
        <ImmoButton groesse={44} icon={Trash} disabled={pruefung === 'laedt'} onClick={() => { void starteLoeschen(); }}>
          {pruefung === 'laedt' ? 'Zuordnungen werden geprüft …' : 'Eigentümer löschen'}
        </ImmoButton>
      </div>

      {pruefung && pruefung !== 'laedt' && !loeschenMoeglich(pruefung) && (
        <>
          <InnenKarte style={GLAS}>
            <div className="flex items-start" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--acc-soft)' }}>
                <Sparkle weight="fill" size={18} color="var(--acc)" />
              </span>
              <div className="flex flex-col" style={{ gap: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 800 }}>Löschen nicht möglich</span>
                <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>{loeschText(s.name, pruefung)}</span>
              </div>
            </div>
          </InnenKarte>
          <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
            <div style={{ padding: '0 6px 6px' }}><KartenTitel>Zuordnungen</KartenTitel></div>
            {pruefung.objekte.map(o => <PanelZeile key={o.objektId} icon={LinkSimple} ton="bad" titel={o.objektName} sub={o.detail} />)}
          </InnenKarte>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="akzent" groesse={44} icon={Check} onClick={() => setPruefung(null)}>Verstanden</ImmoButton>
            <ImmoButton groesse={44} icon={HouseLine} onClick={() => s.oeffnePanel('objekte')}>Zuordnungen verwalten</ImmoButton>
            {!inaktiv && <ImmoButton groesse={44} icon={PauseCircle} disabled={laeuft} onClick={() => { void setzeStatus(false); }}>Stattdessen inaktiv setzen</ImmoButton>}
          </div>
        </>
      )}
      {pruefung && pruefung !== 'laedt' && loeschenMoeglich(pruefung) && (
        <BestaetigenDialog titel="Eigentümer löschen?"
          text={`Möchten Sie ${s.name} endgültig löschen? Kontaktwege, Notizen und protokollierte Aktivitäten werden mit gelöscht, analysierte E-Mails bleiben ohne Zuordnung erhalten. Dieser Schritt kann nicht rückgängig gemacht werden.`}
          bestaetigenLabel="Endgültig löschen" icon={Trash} laeuft={loescht} onBestaetigen={() => { void loesche(); }} onAbbrechen={() => setPruefung(null)} />
      )}
      {verwerfen && <Verwerfen text="Die geänderten Stammdaten gehen verloren." onAbbrechen={() => setVerwerfen(false)} />}
    </>
  );
}

// ── Bankverbindung & SEPA (Screen 38) ──────────────────────────────────────────
interface BankEntwurf {
  iban: string; kontoinhaber: string; bic: string; rhythmus: Rhythmus | '';
  glaeubiger: string; mandatsreferenz: string; mandatsdatum: string; kundennummer: string;
}
const KONTO: readonly (keyof BankEntwurf)[] = ['iban', 'kontoinhaber', 'bic', 'rhythmus'];
const SEPA: readonly (keyof BankEntwurf)[] = ['glaeubiger', 'mandatsreferenz', 'mandatsdatum', 'kundennummer'];
const RHYTHMUS_ICON: Record<Rhythmus, Icon> = { monatlich: Calendar, quartalsweise: CalendarDots, jaehrlich: CalendarCheck };

export function BankPanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const w = s.daten.zeile.werte;
  const anfang = (): BankEntwurf => ({
    iban: ibanText(t(eig.bankverbindung)), kontoinhaber: t(eig.kontoinhaber), bic: t(eig.bic),
    rhythmus: istRhythmus(eig.auszahlung_rhythmus) ? eig.auszahlung_rhythmus : '',
    glaeubiger: t(eig.glaeubiger_id), mandatsreferenz: t(eig.sepa_mandatsreferenz), mandatsdatum: t(eig.sepa_mandatsdatum).slice(0, 10), kundennummer: t(eig.kundennummer),
  });
  const [start, setStart] = useState<BankEntwurf>(anfang);
  const [e, setE] = useState<BankEntwurf>(start);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof BankEntwurf>(k: K) => (v: BankEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const anders = (felder: readonly (keyof BankEntwurf)[]) => felder.some(k => e[k].trim() !== start[k].trim());
  const [verwerfen, setVerwerfen] = useVerlassen(anders([...KONTO, ...SEPA]));
  const iban = e.iban.replace(/\s+/g, '');
  const ibanFehler = iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/i.test(iban) ? 'IBAN ist unvollständig' : undefined;
  const bicFehler = e.bic.trim() && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(e.bic.trim()) ? 'BIC hat 8 oder 11 Zeichen' : undefined;
  const anschrift = [[t(eig.strasse), t(eig.hausnummer)].filter(Boolean).join(' '), [t(eig.plz), t(eig.stadt)].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  // Mandat aus den gespeicherten Daten – ungespeicherte Eingaben zählen erst nach „Speichern“
  const mandat: MandatDaten = {
    glaeubigerId: start.glaeubiger, mandatsreferenz: start.mandatsreferenz, mandatsdatum: start.mandatsdatum, kundennummer: start.kundennummer,
    kontoinhaber: start.kontoinhaber || s.name, anschrift, iban: start.iban, bic: start.bic,
  };
  const fehltFuerPdf = mandatFehlt(mandat);

  const speichere = async (teil: 'konto' | 'sepa') => {
    if (teil === 'konto' && (ibanFehler || bicFehler)) { zeige(ibanFehler ?? bicFehler ?? '', true); return; }
    const leer = (x: string) => (x.trim() ? x.trim() : null);
    const felder: Record<string, string | null> = teil === 'konto'
      ? { bankverbindung: leer(ibanText(iban)), kontoinhaber: leer(e.kontoinhaber), bic: leer(e.bic.toUpperCase()), auszahlung_rhythmus: e.rhythmus || null }
      : { glaeubiger_id: leer(e.glaeubiger), sepa_mandatsreferenz: leer(e.mandatsreferenz), sepa_mandatsdatum: e.mandatsdatum || null, kundennummer: leer(e.kundennummer) };
    setLaeuft(true);
    try {
      await speichereBank(eig.id, felder);
      setStart(alt => ({ ...alt, ...Object.fromEntries((teil === 'konto' ? KONTO : SEPA).map(k => [k, e[k]])) }));
      const rh = RHYTHMEN.find(r => r.key === e.rhythmus)?.label.toLowerCase();
      zeige(teil === 'konto' ? `${w.verwaltet ? 'Auszahlungskonto' : 'Bankverbindung'} gespeichert${w.verwaltet && rh ? ` · ${rh}` : ''}` : 'SEPA-Daten gespeichert');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const pdf = () => {
    // WEG-Hausgeld: Zahlungsempfänger ist die Gemeinschaft, vertreten durch die Verwaltung
    const wegs = s.daten.zeile.zuordnungen.filter(z => z.rolle === 'WEG' || z.rolle === 'WEG+SEV').map(z => z.objektName);
    try { ladeMandatPdf(mandat, mandatEmpfaenger(wegs)); zeige(`SEPA-Lastschriftmandat ${mandat.mandatsreferenz} erstellt`); }
    catch (err) { zeige(`PDF nicht erstellt: ${fehlerText(err)}`, true); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>{w.verwaltet ? 'Auszahlungskonto' : 'Bankverbindung'}</KartenTitel>
        {w.verwaltet && (
          <SegmentedControl<Rhythmus | ''> ariaLabel="Auszahlungsrhythmus" hoehe={34} className="self-start" wert={e.rhythmus} onChange={setze('rhythmus')}
            segmente={RHYTHMEN.map(r => ({ key: r.key, label: r.label, icon: RHYTHMUS_ICON[r.key] }))} />
        )}
        <FeldRaster minBreite={200}>
          <TextFeld label="IBAN" icon={Bank} wert={e.iban} onChange={setze('iban')} placeholder="DE00 …" fehler={ibanFehler} autoComplete="off" />
          <TextFeld label="Kontoinhaber" icon={IdentificationCard} wert={e.kontoinhaber} onChange={setze('kontoinhaber')} placeholder="Name des Kontoinhabers" autoComplete="off" />
          <TextFeld label="BIC" icon={Buildings} wert={e.bic} onChange={setze('bic')} placeholder="z. B. HELADEF1MAR" fehler={bicFehler} autoComplete="off" />
        </FeldRaster>
        {w.verwaltet && !e.rhythmus && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Auszahlungsrhythmus noch nicht festgelegt.</span>}
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !anders(KONTO)} onClick={() => { void speichere('konto'); }}>Speichern</ImmoButton>
        </div>
      </InnenKarte>

      <InnenKarte style={GLAS}>
        <KartenTitel>SEPA &amp; Zahlungsverkehr</KartenTitel>
        <FeldRaster minBreite={220}>
          <TextFeld label="Gläubiger-Identifikationsnummer" icon={IdentificationCard} wert={e.glaeubiger} onChange={setze('glaeubiger')} placeholder="DE98ZZZ…" autoComplete="off" />
          <TextFeld label="SEPA-Mandatsreferenz" icon={Hash} wert={e.mandatsreferenz} onChange={setze('mandatsreferenz')} placeholder="z. B. MND-2026-017" autoComplete="off" />
          <TextFeld label="Mandat erteilt am" icon={CalendarBlank} type="date" wert={e.mandatsdatum} onChange={setze('mandatsdatum')} max={s.heute} />
          <TextFeld label="Kunden-/Objektnummer beim Zahlungsempfänger" icon={Receipt} wert={e.kundennummer} onChange={setze('kundennummer')} placeholder="Nummer beim Zahlungsempfänger" autoComplete="off" voll />
        </FeldRaster>
        <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={44} icon={FilePdf} disabled={fehltFuerPdf.length > 0} onClick={pdf}
            title={fehltFuerPdf.length ? `Für das Mandat fehlt: ${fehltFuerPdf.join(', ')}` : undefined}>Mandat als PDF</ImmoButton>
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !anders(SEPA)} onClick={() => { void speichere('sepa'); }}>SEPA-Daten speichern</ImmoButton>
        </div>
        {fehltFuerPdf.length > 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Für das Mandat als PDF fehlt: {fehltFuerPdf.join(', ')}.</span>}
      </InnenKarte>

      {w.hasWEG && (
        <InnenKarte style={GLAS} gap={0}>
          <KartenTitel icon={Coins}>SEPA-Lastschrift Hausgeld (WEG)</KartenTitel>
          <DatenZeile label="Mandatsreferenz" wert={start.mandatsreferenz || '–'} />
          <DatenZeile label="Konto" wert={start.iban || '–'} />
          <DatenZeile label="Betrag" wert={w.direktHg ? `${eur(w.direktHg)} / Monat` : 'entfällt – Verrechnung aus SEV-Miete'} zahl />
          <DatenZeile label="Status" wert={<span style={{ color: start.mandatsreferenz ? 'var(--ok)' : 'var(--ink3)' }}>
            {start.mandatsreferenz ? `Mandat erteilt${start.mandatsdatum ? ` am ${deDatum(start.mandatsdatum)}` : ''}` : 'Kein Mandat hinterlegt'}</span>} />
        </InnenKarte>
      )}
      {verwerfen && <Verwerfen text="Die geänderte Bankverbindung geht verloren." onAbbrechen={() => setVerwerfen(false)} />}
    </>
  );
}

// ── Eigentümerportal (Screens 39, 50) ──────────────────────────────────────────
export function PortalPanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const w = s.daten.zeile.werte;
  const [toast, zeige] = useToastPill();
  const [mailSenden, setMailSenden] = useState(true);
  const [passwort, setPasswort] = useState('');
  const [sichtbar, setSichtbar] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [sperren, setSperren] = useState(false);
  const [suche, setSuche] = useState('');
  const [loesen, setLoesen] = useState(false);
  const p = PORTAL_ANZEIGE[s.portal];
  // #584: ein verknüpfter Datensatz braucht keine eigene E-Mail (Login über den gemeinsamen Zugang)
  const status = s.portal !== 'kein' ? s.portal : !s.email ? 'ohneEmail' : 'kein';
  const verbund = s.daten.portalVerbund;
  const andere = verbund?.andere ?? [];
  const kandidaten = useMemo(() => {
    const alle = verbund?.kandidaten ?? [];
    const q = suche.trim().toLowerCase();
    // gleiche E-Mail immer, andere Zugänge erst nach Suche (Name oder E-Mail)
    return alle.filter(k => k.gleicheEmail || (q.length >= 2 && kandidatText(k).toLowerCase().includes(q))).slice(0, 8);
  }, [verbund, suche]);
  const gleicheEmail = (verbund?.kandidaten ?? []).some(k => k.gleicheEmail);
  const pFehler = useMemo(() => (passwort ? passwortFehler(passwort) : null), [passwort]);
  // Berechtigungen nach Rolle (Prototyp) – einzeln schaltbar erst mit der Portal-Anbindung
  const rechte: { label: string; icon: Icon }[] = [
    ...(w.verwaltet ? [{ label: 'Eigentümerabrechnung', icon: Receipt }, { label: 'Eigentümerkonto & Auszahlungen', icon: Wallet }, { label: 'Freigaben erteilen', icon: SealCheck }] : []),
    ...(w.hasWEG ? [{ label: 'Hausgeld, WEG-Abrechnung & Wirtschaftsplan', icon: Coins }, { label: 'ETV, Beschlusssammlung & Vollmachten', icon: UsersThree }] : []),
    { label: 'Dokumente einsehen', icon: FolderSimple }, { label: 'Tickets & Schäden einsehen', icon: Wrench },
  ];

  const freigeben = async () => {
    const f = passwortFehler(passwort);
    if (f) { zeige(f, true); return; }
    setLaeuft(true);
    try {
      await gebeEigPortalFrei({ name: s.name, email: s.email, passwort, eigentuemerId: eig.id });
      // #606: Portal gleich für diese Person öffnen (Testphase) – sonst sieht sie nur „bald verfügbar“
      const tf = await testphaseFuerDatensatz('eigentuemerportal', 'eigentuemer', eig.id);
      const tfHinweis = tf ? ` – Portal aber noch nicht freigeschaltet: ${tf}` : '';
      if (mailSenden) {
        try { const r = await sendeZugangsdaten({ rolle: 'eigentuemer', id: eig.id, passwort, neuSetzen: false }); zeige(`Zugang freigeschaltet – ${zugangsMeldung(r, true)}${tfHinweis}`, !r.gesendet || !!tf); }
        catch (e) { zeige(`Zugang freigeschaltet, E-Mail aber nicht gesendet: ${fehlerText(e)} – Passwort bitte selbst mitteilen${tfHinweis}`, true); }
      } else zeige(`Zugang für ${s.name} freigeschaltet – Passwort dem Eigentümer mitteilen${tfHinweis}`, !!tf);
      setPasswort('');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(fehlerText(err), true);
    } finally {
      setLaeuft(false);
    }
  };
  const setzeStatus = async (neu: 'Aktiv' | 'Inaktiv') => {
    setLaeuft(true);
    try {
      await setzeEigPortalStatus({ profilId: verbund?.profilId ?? null, email: s.email }, neu);
      zeige(neu === 'Inaktiv' ? 'Portalzugang gesperrt' : 'Portalzugang wieder freigegeben');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht geändert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
      setSperren(false);
    }
  };

  const verknuepfen = async (k: ZugangKandidat) => {
    setLaeuft(true);
    try {
      await verknuepfeEigPortal(eig.id, k.profilId);
      zeige(`${k.email ? `Mit ${k.email}` : 'Mit dem Zugang'} verknüpft – ${s.name} erscheint dort im Eigentümerportal`);
      setSuche('');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht verknüpft: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const verknuepfungLoesen = async () => {
    setLaeuft(true);
    try {
      await loeseEigPortal(eig.id);
      zeige('Verknüpfung gelöst – der Zugang bleibt für die anderen Eigentümer bestehen');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gelöst: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
      setLoesen(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe minBreite={240}>
        <KpiTile layout="kachel" label="Status" icon={DeviceMobile} ton={p.ton} wertFarbig wert={status === 'ohneEmail' ? 'E-Mail fehlt' : p.label} />
        <KpiTile layout="kachel" label="Login / Einladung an" icon={EnvelopeSimple} ton="info" wert={s.email || '–'} />
      </KpiReihe>

      <InnenKarte style={GLAS}>
        <KartenTitel>{status === 'aktiv' ? 'Zugang verwalten' : status === 'gesperrt' ? 'Zugang gesperrt' : 'Zugang freigeben'}</KartenTitel>
        {status === 'ohneEmail' && (
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Für den Zugang braucht der Eigentümer eine E-Mail-Adresse.</span>
            <ImmoButton icon={User} onClick={() => s.oeffnePanel('kontakt')}>In Stammdaten ergänzen</ImmoButton>
          </div>
        )}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 8 }}>
          {rechte.map(b => <SchalterZeile key={b.label} label={b.label} icon={b.icon} an={status === 'aktiv'} onChange={() => undefined} disabled />)}
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Einzelne Bereiche lassen sich noch nicht getrennt schalten – ein freigeschalteter Zugang sieht alle Bereiche, die es im Portal schon gibt.</span>
        {status === 'aktiv' && <TestphaseKarte art="eigentuemerportal" tabelle="eigentuemer" datensatzId={eig.id} name={s.name} zeige={zeige} />}

        <div className="flex flex-wrap justify-end" style={{ gap: 8 }}>
          {(status === 'aktiv' || status === 'gesperrt') && (
            <NeuesPasswortSenden rolle="eigentuemer" id={eig.id} email={s.email} zeige={zeige} />
          )}
          {status === 'aktiv' && <ImmoButton art="bad" icon={LockSimple} disabled={laeuft} onClick={() => setSperren(true)}>Zugang sperren</ImmoButton>}
          {status === 'gesperrt' && <ImmoButton art="akzent" icon={LockSimpleOpen} disabled={laeuft} onClick={() => { void setzeStatus('Aktiv'); }}>Zugang wieder freigeben</ImmoButton>}
        </div>
      </InnenKarte>

      {andere.length > 0 && (
        <InnenKarte style={GLAS}>
          <KartenTitel icon={LinkSimple}>Gemeinsamer Zugang</KartenTitel>
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
            Mit diesem Login sieht der Eigentümer im Portal auch die Objekte von: <strong>{andere.map(a => a.name).join(', ')}</strong>.
            Sperren und „Neues Passwort senden“ gelten für alle.
          </span>
          <div className="flex justify-end">
            <ImmoButton icon={LinkSimple} disabled={laeuft} onClick={() => setLoesen(true)}>Verknüpfung lösen</ImmoButton>
          </div>
        </InnenKarte>
      )}

      {(status === 'kein' || status === 'ohneEmail') && (verbund?.kandidaten.length ?? 0) > 0 && (
        <InnenKarte style={GLAS}>
          <KartenTitel icon={LinkSimple}>Mit bestehendem Zugang verknüpfen</KartenTitel>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {gleicheEmail ? 'Für diese E-Mail gibt es schon einen Eigentümer-Zugang – ein zweiter lässt sich nicht anlegen. ' : ''}
            Verknüpft sieht der Eigentümer die Objekte dieses Datensatzes mit seinem vorhandenen Login (z. B. Privatperson und GmbH unter einem Zugang). Es wird keine E-Mail verschickt.
          </span>
          <TextFeld label="Anderen Zugang suchen (Name oder E-Mail)" wert={suche} onChange={setSuche} placeholder="z. B. Rönnau" />
          {kandidaten.length === 0 && suche.trim().length >= 2 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Kein Eigentümer-Zugang gefunden.</span>}
          {kandidaten.map(k => (
            <div key={k.profilId} className="flex items-center justify-between flex-wrap" style={{ gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--ink)', minWidth: 0, overflowWrap: 'anywhere' }}>
                {kandidatText(k)}{k.status === 'Inaktiv' ? ' (gesperrt)' : ''}
              </span>
              <ImmoButton art="akzent" icon={LinkSimple} disabled={laeuft} onClick={() => { void verknuepfen(k); }}>Verknüpfen</ImmoButton>
            </div>
          ))}
        </InnenKarte>
      )}

      {status === 'kein' && !gleicheEmail && (
        <InnenKarte style={GLAS}>
          <KartenTitel>Oder: Zugang direkt mit Passwort einrichten</KartenTitel>
          <form className="flex flex-col" style={{ gap: 10 }} onSubmit={ev => { ev.preventDefault(); if (!laeuft && passwort && !pFehler) void freigeben(); }}>
            <input type="email" autoComplete="username" value={s.email} readOnly hidden />
            <div className="flex items-end flex-wrap" style={{ gap: 8 }}>
              <div style={{ flex: '1 1 220px' }}>
                <TextFeld label="Sicherheits-Passwort" type={sichtbar ? 'text' : 'password'} wert={passwort} onChange={setPasswort} autoComplete="new-password"
                  fehler={pFehler ?? undefined} placeholder="••••••••" />
              </div>
              <ImmoButton groesse={48} pille={false} icon={sichtbar ? EyeSlash : Eye} aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'} onClick={() => setSichtbar(v => !v)} />
              <ImmoButton groesse={48} pille={false} icon={Sparkle} onClick={() => { setPasswort(passwortVorschlag()); setSichtbar(true); }}>Vorschlagen</ImmoButton>
            </div>
            <SchalterZeile label={`Zugangsdaten per E-Mail an ${s.email} senden (Passwort im Klartext)`} icon={EnvelopeSimple} an={mailSenden} onChange={setMailSenden} />
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Der Eigentümer meldet sich mit seiner E-Mail und diesem Passwort an. Ohne E-Mail bitte das Passwort selbst mitteilen. Das Portal wird damit gleich für diese Person freigeschaltet (Testphase) – für alle anderen bleibt es aus.</span>
            <div className="flex justify-end">
              <ImmoButton type="submit" art="akzent" groesse={44} icon={LockSimpleOpen} disabled={laeuft || !passwort || !!pFehler}>
                {laeuft ? 'Wird angelegt …' : passwort && !pFehler ? 'Zugang freischalten' : 'Min. 6 Zeichen erforderlich'}
              </ImmoButton>
            </div>
          </form>
        </InnenKarte>
      )}

      {sperren && (
        <BestaetigenDialog titel="Zugang sperren?" text={`${s.name} kann sich danach nicht mehr im Eigentümerportal anmelden. Freigeben ist jederzeit möglich.`}
          bestaetigenLabel="Sperren" icon={LockSimple} laeuft={laeuft} onBestaetigen={() => { void setzeStatus('Inaktiv'); }} onAbbrechen={() => setSperren(false)} />
      )}
      {loesen && (
        <BestaetigenDialog titel="Verknüpfung lösen?" text={`Die Objekte von ${s.name} erscheinen danach nicht mehr im gemeinsamen Zugang. Der Login bleibt für ${andere.map(a => a.name).join(', ')} bestehen.`}
          bestaetigenLabel="Lösen" icon={LinkSimple} laeuft={laeuft} onBestaetigen={() => { void verknuepfungLoesen(); }} onAbbrechen={() => setLoesen(false)} />
      )}
    </>
  );
}
